<template lang="pug">

.lock-screen
  form.lock-screen__card(novalidate @submit.prevent="submit")
    .lock-screen__icon
      v-icon(size="26" color="#3473cf") mdi-lock-outline

    h1.lock-screen__title Расписание доступно по паролю
    p.lock-screen__text Пароль можно узнать в учебном учреждении.

    .lock-screen__banner.lock-screen__banner--info(v-if="isCookieBlocked")
      v-icon(size="18" color="#3473cf") mdi-information-outline
      span
        | Браузер не сохранил доступ во встроенном окне.
        |
        a(:href="pageUrl" target="_blank" rel="noopener") Откройте расписание в новой вкладке
        | .

    .lock-screen__banner.lock-screen__banner--info(v-else-if="isPasswordChanged && !waitSeconds")
      v-icon(size="18" color="#3473cf") mdi-information-outline
      span Пароль изменился. Введите новый, чтобы снова открыть расписание.

    .lock-screen__banner.lock-screen__banner--wait(v-if="waitSeconds")
      v-icon(size="18" color="#c2740c") mdi-clock-outline
      span
        | Слишком много попыток. Попробуйте снова через
        |
        b {{ waitText }}
        | .

    label.lock-screen__label(for="publication-password") Пароль
    .lock-screen__field(
      :class="{ 'lock-screen__field--error': isWrong, 'lock-screen__field--disabled': !!waitSeconds }"
    )
      input#publication-password(
        ref="input"
        v-model="password"
        :type="isShown ? 'text' : 'password'"
        :disabled="!!waitSeconds"
        autocomplete="current-password"
        autocapitalize="off"
        spellcheck="false"
        @input="resetErrors"
      )
      button.lock-screen__eye(
        type="button"
        :disabled="!!waitSeconds"
        :aria-label="isShown ? 'Скрыть пароль' : 'Показать пароль'"
        @click="isShown = !isShown"
      )
        v-icon(size="20") {{ isShown ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}

    .lock-screen__error(v-if="isWrong")
      v-icon(size="16" color="#b42318") mdi-alert-circle-outline
      span Пароль не подошёл. Проверьте раскладку клавиатуры.

    .lock-screen__error(v-if="isFailed")
      v-icon(size="16" color="#b42318") mdi-alert-circle-outline
      span Не удалось проверить пароль. Проверьте интернет и попробуйте ещё раз.

    button.lock-screen__submit(type="submit" :disabled="!canSubmit")
      | {{ isLoading ? 'Проверяем…' : 'Открыть расписание' }}

    p.lock-screen__foot Устройство запомнит пароль на 30 дней

</template>

<script>
import API from '@/api'
import { access } from '@/common/access'

// Если сервер не прислал Retry-After, ждём столько же, сколько длится окно лимита попыток
const DEFAULT_WAIT_SECONDS = 300

// Отметка о принятом пароле перед перезагрузкой: если сразу после неё форма открылась снова,
// браузер не сохранил cookie доступа — так бывает в iframe, где Safari режет сторонние cookie
const UNLOCKED_AT_KEY = 'publication-unlocked-at'
const UNLOCK_RELOAD_MS = 30 * 1000

function isEmbedded() {
  try {
    return window.self !== window.top
  } catch {
    // Доступ к top из чужого домена запрещён — значит, страница внутри iframe
    return true
  }
}

// sessionStorage бывает недоступен (приватный режим, запрет хранилища в iframe) — тогда отметки просто нет
function markUnlocked() {
  try {
    sessionStorage.setItem(UNLOCKED_AT_KEY, String(Date.now()))
  } catch {
    // без отметки форма просто не подскажет про новую вкладку
  }
}

function takeUnlockedAt() {
  try {
    const unlockedAt = Number(sessionStorage.getItem(UNLOCKED_AT_KEY))
    sessionStorage.removeItem(UNLOCKED_AT_KEY)

    return unlockedAt
  } catch {
    return 0
  }
}

export default {
  name: 'LockScreen',

  data() {
    return {
      password: '',
      isShown: false,
      isLoading: false,
      isWrong: false,
      isFailed: false,
      waitSeconds: 0,
      waitUntil: 0,
      waitTimer: null,
      isCookieBlocked: false,
      pageUrl: window.location.href,
    }
  },

  computed: {
    isPasswordChanged() {
      return access.reason === 'PasswordChanged'
    },

    canSubmit() {
      return !!this.password.trim() && !this.isLoading && !this.waitSeconds
    },

    waitText() {
      const minutes = Math.floor(this.waitSeconds / 60)
      const seconds = String(this.waitSeconds % 60).padStart(2, '0')

      return `${minutes}:${seconds}`
    },
  },

  mounted() {
    const unlockedAt = takeUnlockedAt()

    this.isCookieBlocked = isEmbedded() && Date.now() - unlockedAt < UNLOCK_RELOAD_MS
    this.$refs.input.focus()
    document.addEventListener('visibilitychange', this.updateWait)
  },

  beforeDestroy() {
    clearInterval(this.waitTimer)
    document.removeEventListener('visibilitychange', this.updateWait)
  },

  methods: {
    resetErrors() {
      this.isWrong = false
      this.isFailed = false
    },

    async submit() {
      if (!this.canSubmit) return

      this.isLoading = true
      this.resetErrors()

      try {
        await API.unlock(this.password)
      } catch (error) {
        this.isLoading = false
        this.handleError(error?.response)
        return
      }

      // Перезагрузка, а не повторная навигация: все данные загружаются заново уже с cookie доступа.
      // Хэш возвращается заранее — иначе вместо открытой по ссылке страницы откроется главная
      if (access.returnPath) window.location.hash = access.returnPath

      markUnlocked()
      window.location.reload()
    },

    handleError(response) {
      if (response?.status === 429) {
        this.startWait(parseInt(response.headers?.['retry-after'], 10) || DEFAULT_WAIT_SECONDS)
        return
      }

      if (response?.status === 400) {
        this.isWrong = true
        this.$nextTick(() => this.$refs.input.select())
        return
      }

      this.isFailed = true
    },

    startWait(seconds) {
      clearInterval(this.waitTimer)
      this.waitUntil = Date.now() + seconds * 1000
      this.updateWait()

      if (this.waitSeconds) this.waitTimer = setInterval(this.updateWait, 1000)
    },

    updateWait() {
      if (!this.waitUntil) return

      // В фоне и после сна устройства таймер срабатывает реже, но срок блокировки не меняется.
      this.waitSeconds = Math.max(0, Math.ceil((this.waitUntil - Date.now()) / 1000))

      if (!this.waitSeconds) {
        clearInterval(this.waitTimer)
        this.waitTimer = null
        this.waitUntil = 0
        this.$nextTick(() => this.$refs.input?.focus())
      }
    },
  },
}
</script>

<style lang="sass" scoped>

.lock-screen
  display: flex
  align-items: center
  justify-content: center
  min-height: calc(100vh - 50px)
  padding: 24px 16px

.lock-screen__card
  width: 100%
  max-width: 380px
  padding: 32px 28px
  background: #fff
  border: 1px solid #e2e8f0
  border-radius: 12px
  box-shadow: 0 1px 3px rgba(15, 23, 42, .06)

.lock-screen__icon
  display: flex
  align-items: center
  justify-content: center
  width: 48px
  height: 48px
  margin-bottom: 16px
  border-radius: 12px
  background: #eaf1fb

.lock-screen__title
  margin: 0
  font-size: 20px
  font-weight: 600
  line-height: 1.3
  color: #0f172a

.lock-screen__text
  margin: 6px 0 20px
  font-size: 15px
  color: #475569

.lock-screen__banner
  display: flex
  align-items: flex-start
  gap: 10px
  margin: -4px 0 16px
  padding: 12px 14px
  border-radius: 8px
  font-size: 14px

  .v-icon
    margin-top: 1px

  a
    color: inherit
    font-weight: 600
    text-decoration: underline

  &--info
    background: #eaf1fb
    color: #234f8f

  &--wait
    background: #fefbea
    border: 1px solid #fbe7a6
    color: #8a5a0c

.lock-screen__label
  display: block
  margin-bottom: 6px
  font-size: 14px
  font-weight: 500
  color: #334155

.lock-screen__field
  display: flex
  align-items: center
  height: 48px
  padding: 0 4px 0 14px
  border: 1px solid #cbd5e1
  border-radius: 8px
  background: #fff
  transition: border-color .15s ease, box-shadow .15s ease

  &:focus-within
    border-color: #3473cf
    box-shadow: 0 0 0 3px rgba(52, 115, 207, .18)

  input
    flex: 1
    min-width: 0
    height: 100%
    border: 0
    outline: 0
    background: transparent
    // 16px — меньше iOS увеличивает страницу при фокусе на поле
    font-size: 16px
    color: #0f172a

  &--error
    border-color: #dc2626

    &:focus-within
      border-color: #dc2626
      box-shadow: 0 0 0 3px rgba(220, 38, 38, .15)

  &--disabled
    border-color: #e2e8f0
    background: #f1f5f9

    input
      color: #94a3b8

.lock-screen__eye
  display: flex
  align-items: center
  justify-content: center
  width: 40px
  height: 40px
  border-radius: 6px
  cursor: pointer

  &:disabled
    cursor: not-allowed
    opacity: .4

  @media (hover: hover)
    &:hover:not(:disabled)
      background: #f1f5f9

.lock-screen__error
  display: flex
  align-items: flex-start
  gap: 6px
  margin-top: 8px
  font-size: 14px
  color: #b42318

  .v-icon
    margin-top: 2px

.lock-screen__submit
  width: 100%
  height: 48px
  margin-top: 16px
  border-radius: 8px
  background: #3473cf
  color: #fff
  font-size: 16px
  font-weight: 600
  cursor: pointer
  transition: background .15s ease

  &:disabled
    background: #cbd5e1
    cursor: not-allowed

  @media (hover: hover)
    &:hover:not(:disabled)
      background: #2c63b5

.lock-screen__foot
  margin: 14px 0 0
  font-size: 13px
  color: #64748b
  text-align: center

@media (max-width: 480px)
  .lock-screen
    align-items: flex-start
    padding-top: 40px

  .lock-screen__card
    padding: 0
    border: 0
    background: transparent
    box-shadow: none

</style>
